import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, Monitor, FileText, Sparkles, Trash2, ShieldCheck, ShieldOff, Plus, ExternalLink, Smartphone, Upload, CheckCircle2 } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [deleting, setDeleting] = useState(null);
  // --- Canva Curated Library admin state ---
  const CURATED_CATEGORIES = [
    "menus", "restaurant", "drinks", "coffee", "bar", "pizza", "burgers", "bakery",
    "promotions", "hours", "welcome", "specials", "holiday", "event",
  ];
  const [curated, setCurated] = useState([]);
  const [curatedCategory, setCuratedCategory] = useState("menus");
  const [newCanvaUrl, setNewCanvaUrl] = useState("");
  const [newCanvaTags, setNewCanvaTags] = useState("");
  const [addingCurated, setAddingCurated] = useState(false);

  const loadCurated = async () => {
    try {
      const { data } = await api.get("/canva/curated", { params: { limit: 200 } });
      setCurated(data?.items || []);
    } catch (err) { /* silent */ }
  };

  const addCurated = async (e) => {
    e?.preventDefault?.();
    const url = (newCanvaUrl || "").trim();
    if (!url || !/canva\.com\/design\//i.test(url)) {
      toast.error("Paste a Canva design URL (canva.com/design/…)");
      return;
    }
    setAddingCurated(true);
    try {
      const tags = newCanvaTags.split(",").map((t) => t.trim()).filter(Boolean);
      await api.post("/canva/curated", {
        canva_share_url: url,
        category: curatedCategory,
        tags,
      });
      toast.success("Template added to library");
      setNewCanvaUrl("");
      setNewCanvaTags("");
      await loadCurated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddingCurated(false);
    }
  };

  const deleteCurated = async (tid) => {
    if (!window.confirm("Remove this template from the curated library?")) return;
    try {
      await api.delete(`/canva/curated/${tid}`);
      toast.success("Removed");
      await loadCurated();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [renderingHdId, setRenderingHdId] = useState(null);
  const renderCuratedHd = async (tid) => {
    setRenderingHdId(tid);
    const tToast = toast.loading("Queuing HD render…");
    try {
      await api.post(`/canva/curated/${tid}/render-hd`);
      toast.success("HD render queued · status will update shortly", { id: tToast });
      // Poll the curated list a few times so the admin sees the badge flip
      // from RENDERING → HD (or → SD on failure) without needing to refresh.
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        await loadCurated();
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Render failed", { id: tToast });
    } finally {
      setRenderingHdId(null);
    }
  };

  const [backfillingHd, setBackfillingHd] = useState(false);
  const backfillAllHd = async () => {
    if (!window.confirm("Render HD for every curated template that isn't already HD? This can take a few minutes.")) return;
    setBackfillingHd(true);
    const tToast = toast.loading("Queuing HD renders…");
    try {
      const { data } = await api.post("/canva/curated/render-hd-all");
      toast.success(`${data?.queued || 0} render${data?.queued === 1 ? "" : "s"} queued — check back in a minute`, { id: tToast });
      // Refresh after a short delay so admins see progress.
      setTimeout(loadCurated, 3000);
    } catch (err) {
      toast.error(formatApiError(err) || "Backfill failed", { id: tToast });
    } finally {
      setBackfillingHd(false);
    }
  };

  // --- Launcher APK admin state ---
  const [launcher, setLauncher] = useState(null);
  const [uploadingApk, setUploadingApk] = useState(false);
  const [apkVersion, setApkVersion] = useState("");

  const loadLauncher = async () => {
    try {
      const { data } = await api.get("/admin/launcher");
      setLauncher(data);
      if (data?.version) setApkVersion(data.version);
    } catch (err) { /* silent */ }
  };

  const onApkUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".apk")) {
      toast.error("Please choose a .apk file");
      return;
    }
    setUploadingApk(true);
    const tToast = toast.loading(`Uploading ${file.name}…`);
    try {
      const form = new FormData();
      form.append("apk", file);
      const params = apkVersion ? `?version=${encodeURIComponent(apkVersion)}` : "";
      const { data } = await api.post(`/admin/launcher/upload${params}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLauncher(data);
      toast.success(`APK v${data.version} live · ${data.size_mb} MB`, { id: tToast });
    } catch (err) {
      toast.error(formatApiError(err) || "Upload failed", { id: tToast });
    } finally {
      setUploadingApk(false);
    }
  };

  const removeApk = async () => {
    if (!window.confirm("Remove the uploaded APK? The /download page will be unavailable until a new one is uploaded.")) return;
    try {
      await api.delete("/admin/launcher");
      toast.success("APK removed");
      await loadLauncher();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const load = async () => {
    try {
      const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
      setStats(s.data);
      setUsers(u.data.users || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { if (user && user.role === "admin") { load(); loadLauncher(); loadCurated(); } }, [user]);

  if (user === null) return null;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const updateUser = async (id, body) => {
    try {
      await api.put(`/admin/users/${id}`, body);
      toast.success("Updated");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeUser = async () => {
    try {
      await api.delete(`/admin/users/${deleting.id}`);
      toast.success("User deleted");
      setDeleting(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const cards = stats ? [
    { label: "Total users", value: stats.users, icon: Users, testid: "stat-users" },
    { label: "Pro subscribers", value: stats.users_pro, icon: Sparkles, testid: "stat-pro" },
    { label: "Screens paired", value: stats.screens_paired, icon: Monitor, testid: "stat-screens" },
    { label: "Designs created", value: stats.designs, icon: FileText, testid: "stat-designs" },
  ] : [];

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-7xl text-slate-900">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-3">Admin</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold" style={headingFont}>
            Customers &amp;{" "}
            <span style={accentFont} className="text-sky-600">system</span>.
          </h1>
          <p className="mt-3 text-sm text-slate-500">Visible only to accounts with the <span className="font-semibold text-slate-900">admin</span> role.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {cards.map((c) => (
            <div key={c.label} className="rounded-2xl p-5 border backdrop-blur-xl" style={{ background: "#FFFFFF", borderColor: "#E2E8F0" }} data-testid={c.testid}>
              <c.icon className="h-4 w-4 text-sky-600 mb-5" />
              <div className="text-5xl mb-1 tracking-tight" style={headingFont}>{c.value}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">{c.label}</div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border backdrop-blur-xl overflow-x-auto" style={{ background: "#FFFFFF", borderColor: "#E2E8F0" }} data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="border-b text-xs uppercase tracking-[0.2em] text-slate-500" style={{ borderColor: "#E2E8F0" }}>
              <tr>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Business</th>
                <th className="text-left px-4 py-3 font-medium">Plan</th>
                <th className="text-left px-4 py-3 font-medium">Role</th>
                <th className="text-right px-4 py-3 font-medium">Screens</th>
                <th className="text-right px-4 py-3 font-medium">Designs</th>
                <th className="text-left px-4 py-3 font-medium">Joined</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b hover:bg-slate-50" style={{ borderColor: "rgba(255,255,255,0.04)" }} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium truncate max-w-[220px] text-slate-800">{u.email}</td>
                  <td className="px-4 py-3 text-slate-500 truncate max-w-[180px]">{u.business_name || "—"}</td>
                  <td className="px-4 py-3">
                    <Select value={u.plan} onValueChange={(v) => updateUser(u.id, { plan: v })}>
                      <SelectTrigger className="rounded-xl h-8 w-24 bg-slate-50 border-slate-200 text-slate-900" data-testid={`plan-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    {u.role === "admin" ? (
                      <Button size="sm" variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100 gap-1" onClick={() => updateUser(u.id, { role: "user" })} disabled={u.id === user.id} data-testid={`demote-${u.id}`}>
                        <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />Admin
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100 gap-1" onClick={() => updateUser(u.id, { role: "admin" })} data-testid={`promote-${u.id}`}>
                        <ShieldOff className="h-3.5 w-3.5 text-slate-500" />User
                      </Button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-xl text-slate-900" style={headingFont}>{u.screens}</td>
                  <td className="px-4 py-3 text-right text-xl text-slate-900" style={headingFont}>{u.designs}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" size="sm" className="rounded-full bg-slate-50 border-slate-200 text-rose-500 hover:bg-rose-50 hover:text-rose-700" disabled={u.id === user.id} onClick={() => setDeleting(u)} data-testid={`delete-${u.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-slate-500">No users yet</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ─── Nexso TV Launcher (.apk) ─── */}
        <div className="mt-12" data-testid="launcher-admin-section">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-sky-600 font-semibold">TV Launcher APK</div>
              <h2 className="text-2xl mt-1 tracking-tight font-semibold" style={headingFont}>
                Android stick launcher · <span className="text-slate-400 font-normal">{launcher?.has_apk_on_disk ? `v${launcher.version}` : "not uploaded"}</span>
              </h2>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl">Upload the Nexso .apk here — customers download it from <span className="font-mono text-sky-700">/download</span>. The file is served directly from this backend, so there&apos;s no GitHub round-trip or external dependency.</p>
            </div>
          </div>

          <div className="rounded-2xl border bg-white p-6" style={{ borderColor: "#E2E8F0" }}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center shadow-sm shrink-0">
                  <Smartphone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-lg font-semibold tracking-tight" style={headingFont}>
                      {launcher?.has_apk_on_disk ? `nexso-launcher-v${launcher.version}.apk` : "No APK uploaded yet"}
                    </div>
                    {launcher?.has_apk_on_disk && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid="launcher-live-pill">
                        <CheckCircle2 className="h-3 w-3" /> Live
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 space-x-3">
                    {launcher?.has_apk_on_disk && <span>{launcher.size_mb} MB</span>}
                    {launcher?.storage === "gridfs" && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-sky-700 bg-sky-50 border border-sky-200 px-2 py-0.5 rounded-full" data-testid="launcher-storage-pill">
                        GridFS · persistent
                      </span>
                    )}
                    {launcher?.storage === "disk" && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                        Disk · legacy — re-upload to migrate
                      </span>
                    )}
                    {launcher?.has_apk_on_disk && launcher?.sha256 && (
                      <span className="font-mono">sha256: {String(launcher.sha256).slice(0, 16)}…</span>
                    )}
                    {launcher?.uploaded_at && (
                      <span>uploaded {new Date(launcher.uploaded_at).toLocaleString()}</span>
                    )}
                  </div>
                  {launcher?.uploaded_by && (
                    <div className="text-xs text-slate-400 mt-0.5">by {launcher.uploaded_by}</div>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Input
                  value={apkVersion}
                  onChange={(e) => setApkVersion(e.target.value)}
                  placeholder="1.2.0"
                  className="rounded-xl h-10 w-28 bg-slate-50 border-slate-200 text-slate-900 font-mono text-sm"
                  data-testid="launcher-version-input"
                />
                <label className={`inline-flex items-center gap-2 rounded-full h-10 px-5 font-semibold text-sm cursor-pointer transition-colors ${uploadingApk ? "bg-slate-200 text-slate-500 cursor-not-allowed" : "bg-sky-500 hover:bg-sky-600 text-white"}`} data-testid="launcher-upload-label">
                  <Upload className="h-4 w-4" />
                  {uploadingApk ? "Uploading…" : launcher?.has_apk_on_disk ? "Replace APK" : "Upload APK"}
                  <input
                    type="file"
                    accept=".apk,application/vnd.android.package-archive"
                    className="hidden"
                    onChange={onApkUpload}
                    disabled={uploadingApk}
                    data-testid="launcher-upload-input"
                  />
                </label>
                {launcher?.has_apk_on_disk && (
                  <>
                    <a
                      href="/api/launcher/download"
                      download
                      className="inline-flex items-center gap-1.5 rounded-full h-10 px-4 text-sm font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200"
                      data-testid="launcher-test-download"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> Test download
                    </a>
                    <Button
                      onClick={removeApk}
                      variant="outline"
                      size="sm"
                      className="rounded-full h-10 px-4 text-sm border-rose-200 text-rose-600 hover:bg-rose-50"
                      data-testid="launcher-remove-button"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                    </Button>
                  </>
                )}
              </div>
            </div>

            {!launcher?.has_apk_on_disk && (
              <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900" data-testid="launcher-empty-warning">
                Customers visiting <span className="font-mono">/download</span> will get a 404 until you upload the .apk file.
              </div>
            )}

            {/* OTA info — customers get automatic updates once we bump versionName */}
            {launcher?.has_apk_on_disk && (
              <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-900 flex items-start gap-3" data-testid="launcher-ota-info">
                <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-emerald-600" />
                <div>
                  <div className="font-semibold mb-0.5">Auto-updates are live</div>
                  Every installed stick polls this endpoint every 6 hours (and on every screen-wake) and installs any newer APK automatically. Just bump the version above when uploading a new build — sticks with an older version installed will pick it up on their next check. sha256 &amp; size are verified before install.
                </div>
              </div>
            )}
          </div>

          {/* Step-by-step: how to build & publish a new APK */}
          <details className="mt-4 rounded-2xl border bg-white overflow-hidden group" style={{ borderColor: "#E2E8F0" }} data-testid="launcher-howto">
            <summary className="cursor-pointer list-none px-6 py-4 flex items-center justify-between hover:bg-slate-50">
              <div>
                <div className="text-sm font-semibold tracking-tight text-slate-900" style={headingFont}>How to build &amp; publish a new APK</div>
                <div className="text-xs text-slate-500 mt-0.5">Step-by-step: from source code change → live download link</div>
              </div>
              <div className="text-xs text-sky-600 font-medium group-open:hidden">Show steps ↓</div>
              <div className="text-xs text-sky-600 font-medium hidden group-open:block">Hide ↑</div>
            </summary>

            <div className="border-t p-6 md:p-8 space-y-8 text-sm text-slate-700" style={{ borderColor: "#E2E8F0" }}>
              {/* Step 1 */}
              <div className="flex gap-4">
                <div className="h-8 w-8 rounded-full bg-sky-500 text-white flex items-center justify-center font-semibold text-sm shrink-0">1</div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 mb-1" style={headingFont}>Build the APK on GitHub (recommended)</div>
                  <div className="text-slate-600 mb-2">
                    Every push to <code className="font-mono bg-slate-100 px-1 rounded">main</code> triggers <code className="font-mono bg-slate-100 px-1 rounded">.github/workflows/build-nexso-apk.yml</code>. It compiles the launcher and publishes it as a rolling GitHub Release.
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-slate-600 text-xs mb-2">
                    <li>Open your repo → <strong>Actions</strong> tab → pick the latest <em>“Build Nexso Launcher APK”</em> run.</li>
                    <li>If it&apos;s green, scroll to <strong>Artifacts</strong> → download <code className="font-mono bg-slate-100 px-1 rounded">nexso-launcher-debug</code>.</li>
                    <li>Or grab it from <strong>Releases</strong> → tag <code className="font-mono bg-slate-100 px-1 rounded">nexso-launcher-latest</code>.</li>
                    <li>To rebuild without a code change, click <em>Run workflow</em> on the Actions page.</li>
                  </ul>
                  <div className="text-xs text-slate-500 italic">Alt: build locally with <code className="font-mono bg-slate-100 px-1 rounded">cd android/nexso-launcher &amp;&amp; ./gradlew :app:assembleDebug</code> (requires JDK 17 + Android SDK).</div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4">
                <div className="h-8 w-8 rounded-full bg-sky-500 text-white flex items-center justify-center font-semibold text-sm shrink-0">2</div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 mb-1" style={headingFont}>Upload the .apk above</div>
                  <div className="text-slate-600 mb-2">
                    Set the version (e.g. <code className="font-mono bg-slate-100 px-1 rounded">1.3.0</code>) → click <strong>Upload APK</strong> → pick the <code className="font-mono bg-slate-100 px-1 rounded">app-debug.apk</code> file you just downloaded. On success, this card shows a green <strong>Live</strong> pill.
                  </div>
                  <div className="text-xs text-slate-500">The file is stored in <strong>MongoDB GridFS</strong> (persistent — survives every redeploy) and served via <code className="font-mono bg-slate-100 px-1 rounded">/api/launcher/download</code>. Upload once and never lose the APK again.</div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4">
                <div className="h-8 w-8 rounded-full bg-sky-500 text-white flex items-center justify-center font-semibold text-sm shrink-0">3</div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 mb-1" style={headingFont}>Test the download link</div>
                  <div className="text-slate-600 mb-2">
                    Click <strong>Test download</strong> next to the upload button. Your browser should save a file called <code className="font-mono bg-slate-100 px-1 rounded">nexso-launcher-v{"{ver}"}.apk</code> (not <code>download.txt</code>). If it does, customers will get the same clean file.
                  </div>
                  <div className="text-xs text-slate-500">Verify anonymously: <a className="text-sky-600 underline" href="/download" target="_blank" rel="noreferrer">open /download in a private tab</a> — the big blue button should be enabled and show your version.</div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4">
                <div className="h-8 w-8 rounded-full bg-sky-500 text-white flex items-center justify-center font-semibold text-sm shrink-0">4</div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 mb-1" style={headingFont}>Install on your Android stick</div>
                  <div className="text-slate-600 mb-2">
                    On the TV stick: open a browser → visit <code className="font-mono bg-slate-100 px-1 rounded">nexso.net/download</code> → tap Download → allow the file, then tap it to install.
                  </div>
                  <div className="text-xs text-slate-500">Bulk-install with ADB in one command:</div>
                  <pre className="text-[11px] font-mono bg-slate-900 text-slate-100 rounded-lg px-3 py-2 mt-1 overflow-x-auto">adb connect &lt;stick_ip&gt; &amp;&amp; adb install -r nexso-launcher.apk</pre>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex gap-4">
                <div className="h-8 w-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-semibold text-sm shrink-0">5</div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 mb-1" style={headingFont}>Set Nexso as the default Home app (one time)</div>
                  <div className="text-slate-600 mb-2">
                    After launch, a small blue banner appears in the top-right of the TV: <em>“Set Nexso as your Home app…”</em> → tap <strong>Set as Home</strong> and pick Nexso. Now every TV power-on boots straight into your signage — no manual tap.
                  </div>
                  <div className="text-xs text-slate-500">If the banner didn&apos;t show up, press the Home key on the remote and pick Nexso from the system chooser.</div>
                </div>
              </div>

              {/* Step 6 */}
              <div className="flex gap-4">
                <div className="h-8 w-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-semibold text-sm shrink-0">6</div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 mb-1" style={headingFont}>Program automatic power on/off</div>
                  <div className="text-slate-600 mb-2">
                    Go to <a href="/dashboard/screens" className="text-sky-600 underline">Dashboard → Screens</a> → find your paired screen → click <strong>Hours</strong>. Set open days &amp; hours per location (e.g. Mon-Fri 08:00-20:00). Outside those hours, Nexso goes black &amp; releases wake lock so the TV drops into standby via HDMI-CEC. It wakes back up automatically at the next open window.
                  </div>
                  <div className="text-xs text-slate-500">Requires HDMI-CEC on your TV (also called Anynet+/Bravia Sync/Simplink). Enable it in TV Settings → External Device Manager.</div>
                </div>
              </div>

              {/* Step 7 — OTA rollout */}
              <div className="flex gap-4">
                <div className="h-8 w-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-semibold text-sm shrink-0">7</div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 mb-1" style={headingFont}>Automatic updates (OTA)</div>
                  <div className="text-slate-600 mb-2">
                    Every installed stick polls <code className="font-mono bg-slate-100 px-1 rounded">/api/launcher/latest</code> every 6 hours (and on every foreground). If a newer <code className="font-mono bg-slate-100 px-1 rounded">version</code> is available, the stick downloads the APK, verifies the sha256 + size, and installs it — no visit required.
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-slate-600 text-xs mb-2">
                    <li><strong>To ship an update:</strong> just upload a new APK <em>and bump the version</em> (e.g. 1.3.0 → 1.4.0). Sticks with anything older will update within 6 hours.</li>
                    <li><strong>Consumer devices:</strong> Android shows a one-tap &ldquo;Update this app?&rdquo; confirm dialog — a passer-by taps it.</li>
                    <li><strong>Device Owner sticks</strong> (provisioned via <code className="font-mono bg-slate-100 px-1 rounded">dpm set-device-owner</code> or QR provisioning): install completes <em>silently</em>, zero user interaction.</li>
                    <li>Rollout is safe: if the download is corrupted or the sha256 doesn&apos;t match, the install is aborted and the old version keeps running.</li>
                  </ul>
                  <div className="text-xs text-slate-500 italic">Tip: keep the version numbers monotonically increasing (semver). Rolling back to an older version requires an ADB manual install.</div>
                </div>
              </div>

              {/* Deploy web changes */}
              <div className="rounded-xl bg-sky-50 border border-sky-200 px-4 py-3 text-sm text-sky-900 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-sky-600" />
                <div>
                  <div className="font-semibold">Deploy web changes too</div>
                  Web-side updates (new pages, wake-lock bridge, tips) only reach <span className="font-mono">nexso.net</span> after you redeploy the app. Use the <strong>Deploy</strong> button in your Emergent chat or the <strong>Save to GitHub</strong> action.
                </div>
              </div>
            </div>
          </details>
        </div>

        {/* ─── Canva Curated Template Library ─── */}
        <div className="mt-12">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-violet-600 font-semibold">Canva curated library</div>
              <h2 className="text-2xl mt-1 tracking-tight font-semibold" style={headingFont}>
                Featured templates · <span className="text-slate-400 font-normal">{curated.length}</span>
              </h2>
              <p className="text-xs text-slate-500 mt-1">Add Canva design share URLs here. Anyone signed in to Nexso will see them when picking a category. HD renders happen automatically when you add a template.</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={backfillingHd || curated.length === 0}
              onClick={backfillAllHd}
              className="rounded-full bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 h-9 px-4 text-xs font-semibold whitespace-nowrap"
              data-testid="render-hd-all-button"
              title="Re-render every curated template that isn't already HD"
            >
              {backfillingHd ? "Queuing…" : "Render HD for all"}
            </Button>
          </div>

          {/* Add form */}
          <form onSubmit={addCurated} className="rounded-2xl border bg-white p-5 mb-5 grid sm:grid-cols-12 gap-3 items-end" style={{ borderColor: "#E2E8F0" }} data-testid="curated-add-form">
            <div className="sm:col-span-5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Canva design URL</Label>
              <Input
                type="url"
                value={newCanvaUrl}
                onChange={(e) => setNewCanvaUrl(e.target.value)}
                placeholder="https://www.canva.com/design/DAF…/view"
                className="rounded-xl h-10 mt-1 text-sm"
                disabled={addingCurated}
                data-testid="curated-url-input"
              />
            </div>
            <div className="sm:col-span-3">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Category</Label>
              <select
                value={curatedCategory}
                onChange={(e) => setCuratedCategory(e.target.value)}
                className="w-full rounded-xl h-10 mt-1 text-sm border border-slate-200 bg-white px-3 capitalize"
                data-testid="curated-category-select"
              >
                {CURATED_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Tags (csv)</Label>
              <Input
                value={newCanvaTags}
                onChange={(e) => setNewCanvaTags(e.target.value)}
                placeholder="modern, dark, fall"
                className="rounded-xl h-10 mt-1 text-sm"
                data-testid="curated-tags-input"
              />
            </div>
            <div className="sm:col-span-2">
              <Button
                type="submit"
                disabled={addingCurated}
                className="w-full rounded-full bg-violet-600 hover:bg-violet-700 text-white h-10 text-sm font-semibold"
                data-testid="curated-add-submit"
              >
                <Plus className="h-4 w-4 mr-1" />
                {addingCurated ? "Adding…" : "Add to library"}
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 sm:col-span-12 -mt-1 leading-relaxed">
              In Canva, click <span className="font-semibold text-slate-600">Share → Anyone with the link → Can use as template</span>, copy the link, paste it above. We auto-fetch the thumbnail and name.
            </p>
          </form>

          {/* Existing curated entries */}
          {curated.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-10 text-center text-sm text-slate-500" data-testid="curated-empty">
              No curated templates yet. Add one above.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="curated-admin-grid">
              {curated.map((t) => {
                const hd = t.hd_status === "ready";
                const hdPending = t.hd_status === "pending";
                const hdFailed = t.hd_status === "failed" || t.hd_status === "unavailable";
                const isRendering = renderingHdId === t.id;
                return (
                <div key={t.id} className="rounded-2xl border bg-white overflow-hidden group" style={{ borderColor: "#E2E8F0" }}>
                  <div className="aspect-video w-full overflow-hidden bg-slate-100 flex items-center justify-center relative">
                    {t.thumbnail_url
                      ? <img src={t.thumbnail_url} alt={t.name} className="w-full h-full object-contain" loading="lazy" />
                      : <Sparkles className="h-6 w-6 text-slate-300" />}
                    <span
                      className={`absolute top-2 right-2 rounded-full px-2 h-5 text-[10px] font-bold tracking-wide flex items-center ${
                        hd ? "bg-emerald-500 text-white"
                        : hdPending ? "bg-amber-100 text-amber-800 border border-amber-300"
                        : hdFailed ? "bg-rose-100 text-rose-700 border border-rose-300"
                        : "bg-slate-200 text-slate-600"
                      }`}
                      data-testid={`curated-hd-status-${t.id}`}
                    >
                      {hd ? "HD" : hdPending ? "RENDERING" : hdFailed ? "SD" : "SD"}
                    </span>
                  </div>
                  <div className="p-3">
                    <div className="text-xs font-semibold text-slate-900 truncate" title={t.name}>{t.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{t.category}</div>
                    <div className="flex gap-2 mt-2">
                      <a href={t.canva_share_url} target="_blank" rel="noreferrer" className="flex-1">
                        <Button size="sm" variant="outline" className="w-full rounded-full bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 h-7 text-[11px]">
                          <ExternalLink className="h-3 w-3 mr-1" />Open
                        </Button>
                      </a>
                      {!hd && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isRendering || hdPending}
                          onClick={() => renderCuratedHd(t.id)}
                          className="rounded-full bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 h-7 px-2 text-[11px] font-semibold"
                          title="Re-export at native HD resolution"
                          data-testid={`curated-render-hd-${t.id}`}
                        >
                          {isRendering ? "…" : hdPending ? "…" : "HD"}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteCurated(t.id)}
                        className="rounded-full bg-rose-50 border-rose-100 text-rose-600 hover:bg-rose-100 h-7 px-2"
                        title="Remove from library"
                        data-testid={`curated-delete-${t.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="rounded-2xl bg-white border-slate-200 text-slate-900">
          <DialogHeader><DialogTitle style={headingFont}>Delete this user?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">{deleting?.email}</span> and all their designs &amp; groups will be removed. Paired screens will be unpaired.
          </p>
          <DialogFooter>
            <Button variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-700 text-slate-900" onClick={removeUser} data-testid="confirm-delete">Delete user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
